import { useSystemMessages } from "@/hooks/useSystemMessages";
import {
  UserAdmissionData,
  UserAdmissionFile,
} from "@/server/backend-api/learningOpportunityUserAdmission";
import { useLogging } from "@/utils/appInsights/client";
import { Assert } from "@/utils/assert";
import { mcn } from "@/utils/mergeClassNames";
import React from "react";
import { ConfirmDialog } from "../Modal/ConfirmDialog";
import { AdmissionProgressCard } from "./AdmissionProgressCard";
import { createAdmissionProgressItems } from "./createAdmissionProgressItems";
import { AdmissionResponseSuccessDialog } from "@/components/Modal";
import {
  AdmissionResponseType,
  useAdmissionResponseSuccess,
} from "@/hooks/useAdmissionResponseSuccess";
import { LightWeightLearningOpportunities } from "@/server/backend-api";
import { buildUrlWithQuery } from "@/utils/url";
import { ContactUsDialog } from "@/components/Modal/ContactUsDialog";
import { useBaseContext } from "@/components/BaseContext";
import { useProfile } from "@/components/HeaderMain/useProfile";
import { AdmissionAdditionalInformation } from "@/server/backend-api/generated";
import { LearningOpportunityPageQueryParams } from "../../pages/learning-opportunity/[id]/[title]";

type Props = {
  userAdmissionData: UserAdmissionData;
  learningOpp: LightWeightLearningOpportunities;
  className?: string;
  userAdmissionFiles?: UserAdmissionFile[];
};

export const AdmissionProgress = (props: Props) => {
  const [showAcceptDialog, setShowAcceptDialog] = React.useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = React.useState(false);
  const [dialogIsLoading, setDialogIsLoading] = React.useState(false);
  const [showContactUs, setShowContactUs] = React.useState(false);
  const [admissionInputData, setAdmissionInputData] = React.useState<
    AdmissionAdditionalInformation[]
  >([]);
  const {
    showSuccessDialogAndRedirect,
    isSuccessDialogOpen,
    admissionResponseType,
  } = useAdmissionResponseSuccess();
  const base = useBaseContext();
  const { profile } = useProfile();
  const items = createAdmissionProgressItems(props.userAdmissionData);
  const { addSystemMessage, FixedSystemMessageList, hasSystemMessages } =
    useSystemMessages();
  const { logException, logErrorTrace } = useLogging();

  async function makeAcceptOrDeclineRequest(
    admissionId: string,
    admissionData: AdmissionAdditionalInformation[],
    acceptOrDecline: "accept" | "decline"
  ): Promise<boolean> {
    try {
      const url =
        acceptOrDecline === "accept"
          ? "/api/admission/accept"
          : "/api/admission/decline";

      const putBody = {
        admissionId: admissionId,
        admissionData: admissionData,
      };

      const resp = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(putBody),
      });
      if (!resp.ok) {
        logErrorTrace(`Got Status = ${resp.status} from call to ${url}`);
        return false;
      }
      return true;
    } catch (e) {
      logException(e, { admissionId, acceptOrDecline });
      addSystemMessage({
        text: "An error occurred. Please try again later.",
        props: {
          className: "col-span-full",
          kind: "error",
        },
      });
      return false;
    }
  }

  const getAdmissionFields = React.useCallback(
    async (admissionId: string): Promise<AdmissionAdditionalInformation[]> => {
      try {
        const url = `/api/admission/fields?admissionId=${admissionId}`;
        const resp = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!resp.ok) {
          logErrorTrace(`Got Status = ${resp.status} from call to ${url}`);
          return [];
        }
        const data = await resp.json();
        return data;
      } catch (e) {
        logException(e, { admissionId });
        addSystemMessage({
          text: "An error occurred. Please try again later.",
          props: {
            className: "col-span-full",
            kind: "error",
          },
        });
        return [];
      }
    },
    [logErrorTrace, logException, addSystemMessage] // Add dependencies here
  );

  React.useEffect(() => {
    async function fetchAdmissionFields() {
      const data = await getAdmissionFields(
        props.userAdmissionData.admissionId
      );
      setAdmissionInputData(data);
    }
    fetchAdmissionFields();
  }, []);

  const redirectUrl = buildUrlWithQuery<LearningOpportunityPageQueryParams>(
    props.learningOpp.url,
    {
      showInitialHelpText: "1",
    }
  );

  return (
    <>
      <ContactUsDialog
        universities={base.memberUniversities}
        supportSettings={base.contactFormSettings}
        learningOppNames={base.learningOppNames}
        profile={profile ?? undefined}
        show={showContactUs}
        onClose={() => setShowContactUs(false)}
        formProps={{
          title: "Question about admission process",
          description:
            "Use this form to contact us about any question or issue you are facing regarding the admission process for this Learning Opportunity.",
          presets: {
            learningOppId: props.learningOpp.url,
          },
        }}
      />
      <AdmissionResponseSuccessDialog
        open={isSuccessDialogOpen}
        admissionResponseType={admissionResponseType}
      />
      <ConfirmDialog
        open={showAcceptDialog}
        title="Accept admission offer"
        description="By clicking accept you accept this offer and confirm your participation in this learning opportunity."
        onClose={() => {
          if (!dialogIsLoading) {
            setShowAcceptDialog(false);
          }
        }}
        confirmType="click"
        confirmActionText="Accept"
        fields={admissionInputData}
        setFields={setAdmissionInputData}
        onConfirmClick={async () => {
          setDialogIsLoading(true);
          Assert.check(props.userAdmissionData.admissionId !== null);
          const success = await makeAcceptOrDeclineRequest(
            props.userAdmissionData.admissionId,
            admissionInputData,
            "accept"
          );
          setShowAcceptDialog(false);
          setDialogIsLoading(false);
          if (success) {
            showSuccessDialogAndRedirect(
              AdmissionResponseType.OfferAccepted,
              redirectUrl
            );
          }
        }}
        onCancelClick={() => setShowAcceptDialog(false)}
        isLoading={dialogIsLoading}
      />

      <ConfirmDialog
        open={showDeclineDialog}
        title="Decline admission offer"
        description="By clicking decline you do not accept the offer to this learning opportunity and therefore cannot participate."
        onClose={() => {
          if (!dialogIsLoading) {
            setShowDeclineDialog(false);
          }
        }}
        confirmType="click"
        confirmActionText="Decline"
        onConfirmClick={async () => {
          setDialogIsLoading(true);
          Assert.check(props.userAdmissionData.admissionId !== null);
          const success = await makeAcceptOrDeclineRequest(
            props.userAdmissionData.admissionId,
            admissionInputData,
            "decline"
          );
          setShowDeclineDialog(false);
          setDialogIsLoading(false);
          if (success) {
            showSuccessDialogAndRedirect(
              AdmissionResponseType.OfferRejected,
              redirectUrl
            );
          }
        }}
        onCancelClick={() => setShowDeclineDialog(false)}
        isLoading={dialogIsLoading}
      />
      <div className={mcn("space-y-lg", props.className)}>
        {items.map((item, index) => (
          <AdmissionProgressCard
            key={index}
            userAdmission={props.userAdmissionData}
            userAdmissionFiles={props.userAdmissionFiles}
            onAcceptOfferClick={() => setShowAcceptDialog(true)}
            onDeclineOfferClick={() => setShowDeclineDialog(true)}
            {...item}
          />
        ))}
        <div className="bg-grey-10 rounded-2 p-lg">
          <div className="text-style-headline5 mb-2xs">Contact us</div>
          <div>
            Have any questions regarding your application? Just ask our Support
            and Service Centre{" "}
            <button
              className="text-style-link1 cursor-pointer"
              onClick={() => {
                setShowContactUs(true);
              }}
            >
              here
            </button>
            .
          </div>
        </div>
      </div>
      {hasSystemMessages && <FixedSystemMessageList />}
    </>
  );
};
